import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Box, CircularProgress, Alert, Container, Typography } from '@mui/material';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminSetup from './pages/AdminSetup';
import { onAuthChange, logoutUser } from './firebase/authService';
import { getUserPermissions } from './firebase/userService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase/config';
import { BranchProvider } from './contexts/BranchContext';

function App() {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminExists, setAdminExists] = useState(null);

  useEffect(() => {
    let unsubscribe;

    const checkAdminAndAuth = async () => {
      try {
        // Check if Firebase is initialized before attempting to use it
        if (!db) {
          console.error('Firebase is not initialized. Please check your .env.local file.');
          setAdminExists(false);
          setLoading(false);
          return;
        }

        // Check if admin exists by getting all users and checking locally
        // This avoids Firestore index issues with where queries
        let hasAdmin = false;
        try {
          const usersRef = collection(db, 'users');
          const querySnapshot = await getDocs(usersRef);
          // Empty snapshot = no users at all = fresh setup needed
          if (querySnapshot.empty) {
            hasAdmin = false;
          } else {
            querySnapshot.forEach((doc) => {
              const userData = doc.data();
              if (userData.userType === 'admin' || userData.role === 'admin') {
                hasAdmin = true;
              }
            });
          }
        } catch (firestoreErr) {
          console.error('Firestore read error:', firestoreErr.code, firestoreErr.message);
          if (firestoreErr.code === 'permission-denied') {
            // Rules are blocking unauthenticated reads — admin likely exists, show login
            hasAdmin = true;
          } else {
            // Network or config error — show setup so user isn't stuck on login with no account
            hasAdmin = false;
          }
        }

        setAdminExists(hasAdmin);

        // Setup auth listener
        unsubscribe = onAuthChange(async (firebaseUser) => {
          if (window.__suppressAuthStateChange) {
            return;
          }
          
          if (firebaseUser) {
            const permsResult = await getUserPermissions(firebaseUser.uid);
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              role: permsResult.data?.userType || 'staff',
            });
            setPermissions(permsResult.data || {});
          } else {
            setUser(null);
            setPermissions(null);
          }
          setLoading(false);
        });
      } catch (err) {
        console.error('Error in checkAdminAndAuth:', err);
        // Unknown top-level error — default to login to avoid setup loop
        setAdminExists(true);
        setLoading(false);
      }
    };

    checkAdminAndAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleLogin = async (userData) => {
    const permsResult = await getUserPermissions(userData.uid);
    setUser(userData);
    setPermissions(permsResult.data || {});
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
    setPermissions(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show admin setup if no admin exists
  if (adminExists === false) {
    // Check if Firebase is configured
    if (!db) {
      return (
        <ThemeProvider>
          <Container maxWidth="md" sx={{ mt: 8 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Firebase Configuration Missing
              </Typography>
              <Typography variant="body2" paragraph>
                The application cannot connect to Firebase because environment variables are not configured.
              </Typography>
              <Typography variant="body2" component="div">
                To fix this:
                <ol style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <li>Create a <code>.env.local</code> file in the project root</li>
                  <li>Copy the contents from <code>.env.example</code></li>
                  <li>Update the values with your Firebase project credentials</li>
                  <li>Restart the development server</li>
                </ol>
              </Typography>
            </Alert>
          </Container>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider>
        <AdminSetup onSetupComplete={() => setAdminExists(true)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <BranchProvider currentUser={user}>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  user ?
                    <Navigate to="/dashboard" replace /> :
                    <Login onLogin={handleLogin} />
                }
              />
              <Route
                path="/"
                element={
                  user ?
                    <Navigate to="/dashboard" replace /> :
                    <Navigate to="/login" replace />
                }
              />
              <Route
                path="/*"
                element={
                  user ?
                    <Dashboard user={user} permissions={permissions} onLogout={handleLogout} /> :
                    <Navigate to="/login" replace />
                }
              />
            </Routes>
          </Router>
        </BranchProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
